import { useMemo, useState } from 'react';
import clsx from 'clsx';
import { format, parseISO } from 'date-fns';
import { useConfirmationWorklist } from '@/components/confirmations/useConfirmationWorklist';
import ConfirmationWorkModal from '@/components/confirmations/ConfirmationWorkModal';
import CustomerCard from '@/components/customer-card/CustomerCard';
import { bookingSubject } from '@/components/customer-card/subject';
import type { CardTone } from '@/components/customer-card/types';
import { cadenceSchedule, studioDayKey } from '@atink/domain';
import {
  buildAppointmentPairs,
  confirmationsDueCount,
  filterWorklistByPair,
  pairScheduleLine,
  pairSummary,
  tabFigures,
  type AppointmentPair,
  type ConfirmationRow,
  type TouchMark,
  type WorklistTab,
} from '@/domain/confirmationWorklist';
import { ensureConfirmationCall } from '@/domain/confirmationFlow';
import { approvedByOpsLabel, approvedOverrideForBooking } from '@/domain/approvals';
import { useBrainStore } from '@/state/brainStore';
import type { Booking } from '@/domain/types';

const TABS: Array<{ key: WorklistTab; label: string; tone: 'gold' | 'red' | 'green' }> = [
  { key: 'first', label: 'Sequence 1', tone: 'gold' },
  { key: 'second', label: 'Sequence 2', tone: 'gold' },
  { key: 'third', label: 'Sequence 3', tone: 'gold' },
  { key: 'reds', label: 'Reds', tone: 'red' },
  { key: 'confirmed', label: 'Confirmed', tone: 'green' },
];

// Staff wording says "Sequence" for a stage, never "call" (Anas, 8 Oct); the
// dial button stays "Call".
function reasonFor(tab: WorklistTab, row: ConfirmationRow): string {
  const base = ((): string => {
    switch (tab) {
      case 'first':
        return row.booking.depositId ? 'Sequence 1 due today' : 'Sequence 1 due today · confirm & nudge the deposit';
      case 'second':
        return 'Sequence 2 due today';
      case 'third':
        return 'Sequence 3 due today · last before Ops';
      case 'reds':
        return '3 sequences · no answer · escalated to Ops';
      case 'confirmed':
        if (row.state === 'confirmed-at-booking') return 'Confirmed at booking · skips the sequences';
        return row.needsDeposit ? 'Confirmed on the call · still held' : 'Confirmed on the call';
    }
  })();
  if (row.overdue && (tab === 'first' || tab === 'second' || tab === 'third')) {
    // Past its own sequence day: say since when, not "due today".
    const s = cadenceSchedule(row.booking.start);
    const since = { first: s.seq1, second: s.seq2, third: s.seq3 }[tab];
    const n = { first: 1, second: 2, third: 3 }[tab];
    return `Waiting · Sequence ${n} since ${format(parseISO(since), 'EEE d MMM')}${tab === 'third' ? ' · last before Ops' : ''}${
      tab === 'first' && !row.booking.depositId ? ' · confirm & nudge the deposit' : ''
    }`;
  }
  return base;
}

const shortDay = (key: string) => format(parseISO(key), 'EEE d MMM');

function touchLabel(t: TouchMark): string {
  if (t.state === 'due') return `Sequence ${t.seq} due ${shortDay(t.day)}`;
  const mark =
    t.state === 'sent' ? (t.outcome === 'confirmed' ? ' ✓' : t.outcome === 'rescheduled' ? ' moved' : ' no answer') : '';
  return `Sequence ${t.seq} ${shortDay(t.day)}${mark}`;
}

/** The date of each touch sent or due, plus the appointment date (vault Diagram 06). */
function TouchDates({ row }: { row: ConfirmationRow }) {
  return (
    <span className="block font-mono text-[10.5px] text-cream-faint mt-1" data-testid="conf-touch-dates">
      {row.touches.map((t) => (
        <span key={t.seq} className={clsx(t.state === 'due' && 'text-gold-bright', t.outcome === 'confirmed' && 'text-mint')}>
          {touchLabel(t)}
          {' · '}
        </span>
      ))}
      <span className="text-cream-soft">Appt {shortDay(row.appointmentDay)}</span>
    </span>
  );
}

const TONE_TEXT: Record<'gold' | 'red' | 'green', string> = {
  gold: 'text-cream',
  red: 'text-blood',
  green: 'text-mint',
};

/** A stage tab drawn as a card: name + count, due line, the pairs it covers. */
function tabCardClass(active: boolean, tone: 'gold' | 'red' | 'green'): string {
  return clsx(
    'min-w-0 text-left rounded-xl px-3.5 py-2.5 flex flex-col gap-0.5 font-inter transition',
    tone === 'gold' && (active ? 'border-2 border-gold-bright bg-gold-bright/10' : 'border border-gold-dim/60 hover:border-gold-bright/60'),
    tone === 'red' && (active ? 'border-2 border-blood bg-blood/10' : 'border border-blood/40 hover:border-blood/70'),
    tone === 'green' && (active ? 'border-2 border-mint bg-mint/10' : 'border border-mint/40 hover:border-mint/70'),
  );
}

/**
 * The appointment day pairs (D06-04 option 3): each pair's progress, clickable
 * to narrow the tabs and the list to it. Click again, or "Show all days", to clear.
 */
function PairStrip({
  pairs,
  selected,
  todayKey,
  onToggle,
  onClear,
}: {
  pairs: AppointmentPair[];
  selected: AppointmentPair | undefined;
  todayKey: string;
  onToggle: (key: string) => void;
  onClear: () => void;
}) {
  if (pairs.length === 0) return null;
  return (
    <div className="mt-5 max-w-6xl flex flex-col gap-2" data-testid="conf-pairs">
      <p className="label-small-caps text-cream-soft" data-testid="conf-pairs-heading">
        Appointment days ·{' '}
        {selected ? (
          <>
            showing {selected.label} only ·{' '}
            <button
              type="button"
              className="underline underline-offset-2 text-gold-bright hover:text-gold"
              onClick={onClear}
              data-testid="conf-pair-clear"
            >
              Show all days
            </button>
          </>
        ) : (
          'click a pair to see just those bookings'
        )}
      </p>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-2.5">
        {pairs.map((p) => {
          const active = selected?.key === p.key;
          const pct = (n: number) => (p.total ? `${(n / p.total) * 100}%` : '0%');
          return (
            <button
              key={p.key}
              type="button"
              aria-pressed={active}
              onClick={() => onToggle(p.key)}
              data-testid={`conf-pair-${p.key}`}
              className={clsx(
                'min-w-0 text-left rounded-xl px-3 py-2.5 sm:px-3.5 sm:py-3 flex flex-col gap-1.5 font-inter transition',
                active ? 'border-2 border-gold-bright bg-gold-bright/10' : 'border border-gold-dim/50 hover:border-gold-bright/60',
              )}
            >
              <span className="text-sm sm:text-[15px] font-semibold text-cream">{p.label}</span>
              <span className="flex h-1.5 rounded-full overflow-hidden bg-ink-soft/50" aria-hidden="true">
                <i className="block bg-mint" style={{ width: pct(p.confirmed) }} data-testid={`conf-pair-bar-confirmed-${p.key}`} />
                {p.started ? (
                  <i className="block bg-orange" style={{ width: pct(p.toConfirm) }} data-testid={`conf-pair-bar-tocall-${p.key}`} />
                ) : null}
                <i className="block bg-blood" style={{ width: pct(p.withOps) }} data-testid={`conf-pair-bar-ops-${p.key}`} />
              </span>
              <span className="text-xs text-cream-soft" data-testid={`conf-pair-summary-${p.key}`}>
                {pairSummary(p)}
              </span>
            </button>
          );
        })}
      </div>
      {selected ? (
        <p className="font-inter text-[13px] text-cream-soft" data-testid="conf-pair-schedule">
          {pairScheduleLine(selected, todayKey)}
        </p>
      ) : null}
    </div>
  );
}

export default function ConfirmationWorklist() {
  // Live per-view read (tv-1vh.12) + the one builder, shared with the LeftRail
  // badge so both always show the same count (tv-2na.11.2).
  const { bundle: confirmationsBundle, worklist, clockIso, assignedStudios } = useConfirmationWorklist();
  const bookings = confirmationsBundle?.bookings ?? {};
  const customers = confirmationsBundle?.customers ?? {};
  const artists = confirmationsBundle?.artists ?? {};
  const studios = confirmationsBundle?.studios ?? {};
  const deposits = confirmationsBundle?.deposits ?? {};
  const overrideRequests = useBrainStore((st) => st.overrideRequests);
  const waivers = confirmationsBundle?.waivers ?? {};

  const [tab, setTab] = useState<WorklistTab>('first');
  const [pairKey, setPairKey] = useState<string | null>(null);
  const [openCallId, setOpenCallId] = useState<string | null>(null);

  const todayKey = studioDayKey(clockIso);
  const pairs = useMemo(
    () => buildAppointmentPairs({ nowIso: clockIso, bookings, studioIds: assignedStudios, worklist }),
    [clockIso, bookings, assignedStudios, worklist],
  );
  // A selected pair that has rolled off the strip (the clock moved on) clears itself.
  const selectedPair: AppointmentPair | undefined = pairKey ? pairs.find((p) => p.key === pairKey) : undefined;
  const selected = selectedPair?.key ?? null;
  const shown = useMemo(() => filterWorklistByPair(worklist, selected), [worklist, selected]);
  const figures = useMemo(
    () => tabFigures({ worklist: shown, pairs, selected, todayKey }),
    [shown, pairs, selected, todayKey],
  );

  const rows: ConfirmationRow[] = shown[tab];

  // The header is always the whole worklist (the LeftRail badge's figure).
  const dueToday = confirmationsDueCount(worklist);

  const openRow = (row: ConfirmationRow) => {
    const id = row.call?.id ?? ensureConfirmationCall(row.booking.id, { at: clockIso });
    if (id) setOpenCallId(id);
  };

  const waiverFor = (bookingId: string) =>
    Object.values(waivers)
      .filter((w) => w.bookingId === bookingId)
      .sort((a, b) => b.createdAt.localeCompare(a.createdAt))[0];

  // B3: a waived booking's chip says who approved it ("Approved by Ops · …").
  const approvalFor = (booking: Booking): { approvalLabel?: string } => {
    if (!booking.depositWaived) return {};
    const approval = approvedOverrideForBooking(booking.id, overrideRequests);
    return approval ? { approvalLabel: approvedByOpsLabel(approval) } : {};
  };

  return (
    <section className="px-4 py-6 sm:px-8 sm:py-8" data-testid="confirmation-worklist">
      <header className="flex items-end justify-between max-w-6xl flex-wrap gap-x-6 gap-y-3">
        <div>
          <p className="label-small-caps text-gold-bright">Confirmations</p>
          <h1 className="heading-display text-3xl text-cream mt-2">Confirm the week ahead</h1>
        </div>
        <p className="font-inter text-sm text-cream-soft" data-testid="conf-header-line">
          <span className="text-cream font-semibold" data-testid="conf-worklist-due-today">{dueToday}</span> due today ·{' '}
          <span className="text-cream font-semibold" data-testid="conf-worklist-ops-count">{worklist.counts.reds}</span> on the
          Ops worklist
        </p>
      </header>

      <PairStrip
        pairs={pairs}
        selected={selectedPair}
        todayKey={todayKey}
        onToggle={(key) => setPairKey((cur) => (cur === key ? null : key))}
        onClear={() => setPairKey(null)}
      />

      <nav
        className="mt-5 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2.5 max-w-6xl border-b border-gold-dim/30 pb-4"
        data-testid="conf-worklist-tabs"
        aria-label="Sequence"
      >
        {TABS.map((t) => {
          const active = tab === t.key;
          const f = figures[t.key];
          return (
            <button
              key={t.key}
              type="button"
              onClick={() => setTab(t.key)}
              aria-pressed={active}
              data-testid={`conf-worklist-tab-${t.key}`}
              className={tabCardClass(active, t.tone)}
            >
              <span className="flex items-baseline justify-between gap-2">
                <span className={clsx('text-sm', active ? 'font-bold' : 'font-semibold', TONE_TEXT[t.tone])}>{t.label}</span>
                <span
                  className={clsx('text-xl', active ? 'font-bold' : 'font-semibold', TONE_TEXT[t.tone])}
                  data-testid={`conf-tab-count-${t.key}`}
                >
                  {f.count}
                </span>
              </span>
              <span className="text-xs text-cream-soft" data-testid={`conf-tab-due-${t.key}`}>
                {f.dueLine}
              </span>
              <span
                className="text-[11.5px] text-cream-faint truncate"
                title={f.pairsLine}
                data-testid={`conf-tab-pairs-${t.key}`}
              >
                {f.pairsLine}
              </span>
            </button>
          );
        })}
      </nav>

      <ol className="mt-5 max-w-4xl space-y-2" data-testid="conf-worklist-rows">
        {rows.length === 0 ? (
          <li className="font-serif text-cream-faint text-sm py-6 text-center">Nothing here — this pile is clear.</li>
        ) : (
          rows.map((row, i) => {
            const booking = row.booking;
            const subject = bookingSubject(booking, {
              customer: customers[booking.customerId],
              artist: artists[booking.artistId],
              studio: studios[booking.studioId],
              deposit: booking.depositId ? deposits[booking.depositId] : undefined,
              waiver: waiverFor(booking.id),
              ...approvalFor(booking),
            });
            const tone: CardTone =
              tab === 'reds' || row.overdue ? 'crit' : tab === 'confirmed' ? 'good' : 'default';
            const actionable = tab !== 'reds' && tab !== 'confirmed';
            return (
              <li key={row.call?.id ?? booking.id}>
                {i === 0 && actionable ? (
                  <p className="label-small-caps text-gold-bright mb-1 ml-1" data-testid="conf-worklist-upnext">
                    Up next
                  </p>
                ) : null}
                <CustomerCard
                  testId={`conf-worklist-row-${row.call?.id ?? booking.id}`}
                  onOpen={actionable ? () => openRow(row) : undefined}
                  tone={tone}
                  subject={subject}
                  role={{
                    reason: (
                      <>
                        {reasonFor(tab, row)}
                        {row.needsDeposit ? (
                          <span
                            className="ml-2 rounded border border-gold-dim/60 px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-widest text-gold-bright"
                            data-testid="conf-needs-deposit"
                          >
                            Needs deposit
                          </span>
                        ) : null}
                        <TouchDates row={row} />
                      </>
                    ),
                    actions: actionable ? (
                      <button className="btn-primary text-xs" type="button" onClick={() => openRow(row)}>
                        Call
                      </button>
                    ) : (
                      <span
                        className={clsx(
                          'font-mono text-[10px] uppercase tracking-widest',
                          tab === 'reds' ? 'text-blood' : 'text-mint',
                        )}
                      >
                        {tab === 'reds' ? 'On the Ops worklist' : 'No action needed'}
                      </span>
                    ),
                  }}
                />
              </li>
            );
          })
        )}
      </ol>

      {openCallId ? <ConfirmationWorkModal callId={openCallId} onClose={() => setOpenCallId(null)} /> : null}
    </section>
  );
}
